import { BookOpen, FileText, Play, Sparkles, ArrowRight } from 'lucide-react'

interface DemoDashboardProps {
  userName?: string
}

export function DemoDashboard({ userName = 'Demo User' }: DemoDashboardProps) {
  const displayName = userName
  const workshops = [
    {
      id: 1,
      title: 'Introduction to securesight.ai',
      description: 'Learn the basics of our platform and how to navigate key features.',
      duration: '15 min',
      level: 'Beginner',
      icon: <BookOpen className="w-5 h-5" />
    },
    {
      id: 2,
      title: 'Creating Effective Presentations',
      description: 'Discover best practices for creating engaging AI-powered presentations.',
      duration: '20 min',
      level: 'Intermediate',
      icon: <Play className="w-5 h-5" />
    },
    {
      id: 3,
      title: 'Team Collaboration Guide',
      description: 'Understand how to collaborate with your team effectively.',
      duration: '25 min',
      level: 'Beginner',
      icon: <Sparkles className="w-5 h-5" />
    }
  ]

  const trialPresentations = [
    {
      id: 1,
      title: 'Product Demo Presentation',
      description: 'Sample presentation showcasing product features and capabilities.',
      slides: 12,
      views: 150
    },
    {
      id: 2,
      title: 'Quarterly Business Review',
      description: 'Template for executive business reviews and metrics.',
      slides: 18,
      views: 89
    },
    {
      id: 3,
      title: 'Training Materials',
      description: 'Employee onboarding and training presentation templates.',
      slides: 24,
      views: 203
    }
  ]

  return (
    <div className="space-y-6">
      {/* Welcome Message */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 p-8 rounded-lg">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-primary/20 rounded-lg">
            <Sparkles className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Welcome to securesight.ai, {displayName}!
            </h1>
            <p className="text-muted-foreground mb-4">
              You're exploring our platform in demo mode. Get access to trial workshops, sample presentations,
              and experience the power of AI-driven content management.
            </p>
            <div className="flex items-center gap-2 text-sm text-primary">
              <Sparkles className="w-4 h-4" />
              <span className="font-medium">Demo Account Active</span>
            </div>
          </div>
        </div>
      </div>

      {/* Guided Tour Card */}
      <div className="bg-card border border-border p-6 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Play className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-1">Take a Guided Tour</h2>
              <p className="text-sm text-muted-foreground">
                Get a comprehensive walkthrough of securesight.ai's features and capabilities.
              </p>
            </div>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium transition-colors hover:bg-primary/90">
            <span>Start Tour</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Workshop Materials Section */}
      <div className="bg-card border border-border p-6 rounded-lg">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg">
            <BookOpen className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Workshop Materials</h2>
            <p className="text-sm text-muted-foreground">Learn at your own pace with our interactive workshops</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {workshops.map((workshop) => (
            <div
              key={workshop.id}
              className="p-5 bg-muted/30 border border-border rounded-lg hover:border-primary/50 transition-colors cursor-pointer group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 bg-primary/10 rounded-lg text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  {workshop.icon}
                </div>
                <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-md font-medium">
                  {workshop.level}
                </span>
              </div>
              <h3 className="text-base font-semibold text-foreground mb-2">{workshop.title}</h3>
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{workshop.description}</p>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Play className="w-3 h-3" />
                  {workshop.duration}
                </span>
                <span className="text-primary font-medium group-hover:underline">Start →</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Trial Presentations Section */}
      <div className="bg-card border border-border p-6 rounded-lg">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg">
            <FileText className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Trial Presentations</h2>
            <p className="text-sm text-muted-foreground">Explore sample presentations and templates</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {trialPresentations.map((presentation) => (
            <div
              key={presentation.id}
              className="p-5 bg-muted/30 border border-border rounded-lg hover:border-primary/50 transition-colors cursor-pointer group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 bg-primary/10 rounded-lg text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <FileText className="w-5 h-5" />
                </div>
                <span className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded-md font-medium">
                  {presentation.slides} slides
                </span>
              </div>
              <h3 className="text-base font-semibold text-foreground mb-2">{presentation.title}</h3>
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{presentation.description}</p>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Play className="w-3 h-3" />
                  {presentation.views} views
                </span>
                <span className="text-primary font-medium group-hover:underline">View →</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Upgrade Prompt */}
      <div className="bg-gradient-to-br from-primary via-primary/90 to-primary/80 border border-primary/30 p-8 rounded-lg text-white">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-6 h-6" />
              <h2 className="text-2xl font-bold">Upgrade to Full Access</h2>
            </div>
            <p className="text-white/90 mb-4 max-w-2xl">
              Ready to unlock the full potential of securesight.ai? Get unlimited access to all features,
              create unlimited presentations, collaborate with your team, and much more.
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center gap-2 text-white/90">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Unlimited presentations and storage
              </li>
              <li className="flex items-center gap-2 text-white/90">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Advanced AI-powered content generation
              </li>
              <li className="flex items-center gap-2 text-white/90">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Team collaboration and user management
              </li>
              <li className="flex items-center gap-2 text-white/90">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Priority support and custom templates
              </li>
            </ul>
            <div className="flex gap-3">
              <button className="px-6 py-3 bg-white text-primary rounded-md font-medium transition-colors hover:bg-white/90">
                Contact Sales
              </button>
              <button className="px-6 py-3 bg-white/20 border border-white/30 text-white rounded-md font-medium transition-colors hover:bg-white/30">
                View Pricing
              </button>
            </div>
          </div>
          <div className="hidden lg:block">
            <svg className="w-24 h-24 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  )
}
